import { c as _c } from "react/compiler-runtime";
import { useMemberSeatsLookup } from "@hooks/use-member-seats-lookup";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { getHeldSeats } from "@utils/member-seats-utils";
import { doesWorkspaceRoleRequireXgSeat } from "@utils/xg-seat-utils";

/**
 * Returns whether a member has to be given a Sphere XG seat before they can take on a workspace
 * role, whether the role is changed for them alone or along with other members.
 *
 * That is only the case on a plan that limits the Sphere XG seats assigned to members (the
 * `ACTIVE_SEAT` constraint), for a role that needs a seat and a member that does not hold the
 * assigned seat yet. Teams cannot hold a seat, so their roles change as before.
 *
 * @param member The member whose role is changed
 * @param role The workspace role the member should take on
 * @param lookup The seats of the workspace, see useMemberSeatsLookup
 */
export function isXgSeatRequiredForRole(member, role, lookup) {
  if (!lookup.isActiveSeatPlan || FaroCoreApiUtils.Member.isTeam(member) || !doesWorkspaceRoleRequireXgSeat(role)) {
    return false;
  }
  return !getHeldSeats(member, lookup).hasXgSeat;
}

/**
 * Returns whether a member has to be given a Sphere XG seat before they can take on a workspace
 * role, see isXgSeatRequiredForRole. Without the member there is nothing to check, so their role
 * changes as before.
 *
 * @param member The member whose role is changed, if known
 */
export function useIsXgSeatRequiredForRole(member) {
  const $ = _c(3);
  const memberSeatsLookup = useMemberSeatsLookup();
  let t0;
  if ($[0] !== member || $[1] !== memberSeatsLookup) {
    t0 = role => !!member && isXgSeatRequiredForRole(member, role, memberSeatsLookup);
    $[0] = member;
    $[1] = memberSeatsLookup;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}